/**
 * Plan-gated features and the cheapest plan that includes each one.
 *
 * Plan membership comes from shared/accessControl.ts PLAN_FEATURES, so this
 * file never restates which plan has what. A few gates are not modelled there
 * (recording, live transcription) and carry their own minimum tier, matching
 * the server gates they mirror (lib/securityGates, shared/liveTranscript).
 * Both are on every plan, Lite and trials included; only an org with no
 * plan is locked out.
 */
import { PLAN_FEATURES, type Feature } from '@shared/accessControl';
import { hasActivePlan } from '@shared/liveTranscript';

export type PlanTier = 'lite' | 'starter' | 'growth';

/** Every gate the UI can lock. accessControl Features plus a few extras. */
export type GatedFeature =
  | Feature
  | 'callRecording'
  | 'liveTranscription'
  | 'aiAfterHours';

/** Cheapest first. The monthly id is the canonical row for each tier. */
export const PLAN_TIERS: { tier: PlanTier; planId: string; label: string }[] = [
  { tier: 'lite', planId: 'lite-plan-monthly', label: 'Lite' },
  { tier: 'starter', planId: 'starter-plan-monthly', label: 'Starter' },
  { tier: 'growth', planId: 'growth-plan-monthly', label: 'Growth' },
];

/** Gates that are not in PLAN_FEATURES, with their minimum tier. */
const EXTRA_MIN_TIER: Record<'callRecording' | 'liveTranscription' | 'aiAfterHours', PlanTier> = {
  // Recording and live transcription are on every plan.
  callRecording: 'lite',
  liveTranscription: 'lite',
  // After-hours AI is the AI receptionist on a schedule.
  aiAfterHours: 'starter',
};

export const FEATURE_LABELS: Record<GatedFeature, string> = {
  sms: 'Texting',
  voice: 'Calling',
  aiVoice: 'The AI receptionist',
  aiAfterHours: 'After-hours AI answering',
  phoneNumbers: 'Extra phone numbers',
  campaigns: 'Campaigns',
  analytics: 'Analytics',
  integrations: 'Integrations',
  api: 'API access',
  teamManagement: 'Team management',
  spamLookup: 'Advanced spam detection (Nomorobo)',
  skipTrace: 'Skip tracing',
  powerDialer: 'The power dialer',
  predictiveDialer: 'The predictive dialer',
  voicemailDrop: 'Voicemail drop',
  answeringMachineDetection: 'Answering machine detection',
  callScreeningAssist: 'Call screening auto-detect',
  holdForMe: 'Hold for me',
  dripCampaigns: 'Drip campaigns',
  aiCompliance: 'AI compliance',
  multiProvider: 'Multi-provider routing',
  callRecording: 'Call recording',
  liveTranscription: 'Live transcription',
};

/**
 * The cheapest LimePhone tier that includes `feature`, or null when no
 * LimePhone plan does (outbound-only modules).
 */
export function requiredPlanFor(feature: GatedFeature): PlanTier | null {
  if (feature in EXTRA_MIN_TIER) return EXTRA_MIN_TIER[feature as keyof typeof EXTRA_MIN_TIER];
  for (const { tier, planId } of PLAN_TIERS) {
    if ((PLAN_FEATURES[planId] || []).includes(feature as Feature)) return tier;
  }
  return null;
}

/** Tiers from `tier` upwards, e.g. starter -> ['Starter', 'Growth']. */
export function plansIncluding(tier: PlanTier | null): string[] {
  if (!tier) return [];
  const i = PLAN_TIERS.findIndex((t) => t.tier === tier);
  return PLAN_TIERS.slice(i).map((t) => t.label);
}

export function tierLabel(tier: PlanTier | null): string | null {
  return PLAN_TIERS.find((t) => t.tier === tier)?.label ?? null;
}

/** "The power dialer is on Growth", "Call recording is on every plan" */
export function upgradeHeadline(feature: GatedFeature): string {
  const plans = plansIncluding(requiredPlanFor(feature));
  const label = FEATURE_LABELS[feature] ?? 'This feature';
  if (!plans.length) return `${label} isn't on your plan`;
  if (plans.length === PLAN_TIERS.length) return `${label} is on every plan`;
  const list = plans.length === 1 ? plans[0] : `${plans.slice(0, -1).join(', ')} and ${plans[plans.length - 1]}`;
  return `${label} is on ${list}`;
}

/** Minimal capability shape read by isFeatureAllowed (from /api/capabilities). */
export interface CapabilityFlags {
  voice?: { recording?: boolean };
  ai?: { voiceAgent?: boolean; transcription?: boolean };
  phoneNumbers?: { canPurchase?: boolean };
  integrations?: { api?: boolean; enabled?: boolean };
}

/**
 * Whether the org may use `feature`. `can` is plan membership from
 * accessControl (useFeatureAccess().can); `caps` is the capabilities payload,
 * which wins where it has a matching switch (it knows about trials and
 * per-org overrides).
 */
export function isFeatureAllowed(
  feature: GatedFeature,
  opts: { planType?: string | null; can: (f: Feature) => boolean; caps?: CapabilityFlags | null },
): boolean {
  const { caps, can } = opts;
  switch (feature) {
    case 'callRecording':
      return caps?.voice?.recording ?? true;
    case 'liveTranscription':
      // Every plan (trials too). Whether it is switched on platform-wide
      // (LIVE_TRANSCRIPTION_ENABLED) is the server's call: the dialer only
      // shows the Transcript control when /in-call-state says available.
      // An unknown plan (still loading) is not locked; 'free' etc. are.
      return !opts.planType || hasActivePlan(opts.planType);
    case 'aiAfterHours':
      return can('aiVoice');
    case 'phoneNumbers':
      return (caps?.phoneNumbers?.canPurchase ?? true) && can('phoneNumbers');
    default:
      return can(feature);
  }
}

/** The bits of a /api/plans row this file reads. */
export interface PlanSummary {
  id: string;
  name: string;
  monthlyPrice: number;
}

/** Catalogue defaults, used until /api/plans answers (or if it fails). */
const DEFAULT_PLAN_PRICES: Record<PlanTier, number> = { lite: 9, starter: 19, growth: 45 };

function priceText(n: number): string {
  return `$${Number(n).toFixed(2).replace(/\.00$/, '')}/mo`;
}

/**
 * Copy for a feature that comes with a plan rather than as an add-on, e.g. the
 * AI receptionist: { priceLabel: "Included in Starter & Growth",
 * line: "Starter ($19/mo) and Growth plans include the AI receptionist." }.
 * Names and the cheapest price are read from /api/plans when present.
 */
export function includedPlanCopy(
  feature: GatedFeature,
  plans: PlanSummary[] | undefined | null,
  noun: string,
): { priceLabel: string; line: string; plans: string[] } {
  const tiers = PLAN_TIERS.slice(PLAN_TIERS.findIndex((t) => t.tier === requiredPlanFor(feature)));
  const rows = Array.isArray(plans) ? plans : [];
  const named = tiers.map((t) => {
    const row = rows.find((p) => p.id === t.planId) || rows.find((p) => String(p.name).toLowerCase() === t.label.toLowerCase());
    return { name: row?.name || t.label, price: row && Number.isFinite(Number(row.monthlyPrice)) ? Number(row.monthlyPrice) : DEFAULT_PLAN_PRICES[t.tier] };
  });
  if (!named.length) return { priceLabel: 'Upgrade required', line: `Upgrade your plan to get ${noun}.`, plans: [] };
  const names = named.map((n) => n.name);
  const first = `${named[0].name} (${priceText(named[0].price)})`;
  const rest = names.slice(1);
  const line = rest.length
    ? `${first} and ${rest.join(' and ')} plans include ${noun}.`
    : `The ${first} plan includes ${noun}.`;
  return { priceLabel: `Included in ${names.join(' & ')}`, line, plans: names };
}

export function aiPlanCopy(plans: PlanSummary[] | undefined | null) {
  return includedPlanCopy('aiVoice', plans, 'the AI receptionist');
}

/**
 * What the upgrade sheet says, or null when it has nothing to say and must not
 * open. A sheet with no explanation (only "Upgrade plan" / "Not now" on a
 * blank panel) reached users: nothing stopped an empty request from opening
 * it, and its text was blanked while the request was cleared mid-animation.
 */
export function upgradeSheetContent(req: {
  feature?: GatedFeature;
  title?: string | null;
  message?: string | null;
} | null | undefined): { title: string; body: string; plans: string[] } | null {
  if (!req) return null;
  const title = (req.title ?? '').trim();
  const message = (req.message ?? '').trim();
  const plans = req.feature ? plansIncluding(requiredPlanFor(req.feature)) : [];
  if (req.feature) {
    const label = FEATURE_LABELS[req.feature] ?? 'This feature';
    const list = plans.length <= 1 ? plans.join('') : `${plans.slice(0, -1).join(', ')} and ${plans[plans.length - 1]}`;
    const body = message || (plans.length
      ? `${label} is included in ${list}. Your current plan doesn't include it. Upgrade to turn it on.`
      : `${label} isn't included in your current plan.`);
    return { title: title || upgradeHeadline(req.feature), body, plans };
  }
  if (!title && !message) return null;
  return { title: title || 'Upgrade required', body: message || "Your current plan doesn't include this. Upgrade to turn it on.", plans };
}
